import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import PostCard from "./PostCard";

function PostList({ category, onBack, onPostClick, user }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPostForm, setShowNewPostForm] = useState(false);
  const [newPostTitle, setNewPostTitle] = useState("");
  const [newPostContent, setNewPostContent] = useState("");
  const [newPostAuthorName, setNewPostAuthorName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Set default author name when form opens
  const getDefaultAuthorName = () => {
    if (user?.isGuest) return "";
    return user?.displayName || user?.email?.split("@")[0] || "";
  };

  useEffect(() => {
    const postsRef = collection(db, "messageBoard", "data", "posts");
    const q = query(
      postsRef,
      where("categoryId", "==", category.id),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const postsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPosts(postsData);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching posts:", err);
        setError("Failed to load posts");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [category.id]);

  const handleSubmitPost = async (e) => {
    e.preventDefault();
    if (!newPostTitle.trim() || !newPostContent.trim() || !newPostAuthorName.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      const postsRef = collection(db, "messageBoard", "data", "posts");
      await addDoc(postsRef, {
        categoryId: category.id,
        title: newPostTitle.trim(),
        content: newPostContent.trim(),
        authorId: user?.uid || "guest",
        authorName: newPostAuthorName.trim(),
        authorIsGuest: user?.isGuest || !user,
        createdAt: serverTimestamp(),
        commentCount: 0,
      });

      setNewPostTitle("");
      setNewPostContent("");
      setNewPostAuthorName("");
      setShowNewPostForm(false);
    } catch (err) {
      console.error("Error creating post:", err);
      setError("Failed to create post. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="post-list-container">
      <div className="post-list-header">
        <button className="back-btn" onClick={onBack}>
          <span>←</span> Back
        </button>
        <div className="category-title">
          <span className="category-icon-lg">{category.icon}</span>
          <div>
            <h2>{category.name}</h2>
            <p className="category-name-si">{category.nameSi}</p>
          </div>
        </div>
        <button
          className="new-post-btn"
          onClick={() => {
            if (!showNewPostForm) {
              setNewPostAuthorName(getDefaultAuthorName());
            }
            setShowNewPostForm(!showNewPostForm);
          }}
        >
          {showNewPostForm ? "Cancel" : "+ New Post"}
        </button>
      </div>

      {showNewPostForm && (
        <form className="new-post-form" onSubmit={handleSubmitPost}>
          <h3>Create New Post</h3>
          <input
            type="text"
            placeholder="Your name..."
            value={newPostAuthorName}
            onChange={(e) => setNewPostAuthorName(e.target.value)}
            maxLength={50}
            required
          />
          <input
            type="text"
            placeholder="Post title..."
            value={newPostTitle}
            onChange={(e) => setNewPostTitle(e.target.value)}
            maxLength={100}
            required
          />
          <textarea
            placeholder="What would you like to share or ask?"
            value={newPostContent}
            onChange={(e) => setNewPostContent(e.target.value)}
            rows={5}
            required
          />
          {error && <p className="form-error">{error}</p>}
          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={() => setShowNewPostForm(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || !newPostAuthorName.trim() || !newPostTitle.trim() || !newPostContent.trim()}
            >
              {submitting ? "Posting..." : "Post"}
            </button>
          </div>
        </form>
      )}

      <div className="posts-list">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner-small"></div>
            <p>Loading posts...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">📝</span>
            <p>No posts yet in this category.</p>
            <p className="empty-hint">Be the first to start a discussion!</p>
          </div>
        ) : (
          posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onClick={() => onPostClick(post)}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default PostList;
